import { derived } from 'svelte/store';
import { max } from "d3-array";
import data from "@data/treemap.json";
import { topFourDepartments, budgetBlocks } from "@stores/userData.js";
import departments from "@data/departments.json";

export const getParentOfLevel = (node, level = "department") => {
  if (node.parent && node.data.level && node.data.level !== level) {
    return getParentOfLevel(node.parent, level);
  } else {
    return node;
  }
};

export function traverseUntil(node, filterFn) {
  if (node.children && !filterFn(node)) {
    return node.children.map((item) => traverseUntil(item, filterFn));
  } else {
    return node;
  }
}

export const types = Object.fromEntries(departments.map((dept) => [dept["Department"], dept["Department Type"]]));

export const dataWithIsSelected = derived(
  [topFourDepartments],
  ([$topFourDepartments]) => {
    return data.children
      .map((d) => ({
        ...d,
        isSelected: $topFourDepartments
          .map((d) => d.Department)
          .includes(d.name),
        departmentType: types[d.name],
      }))
      .sort((a, b) => b.isSelected - a.isSelected)
  }
)
export const departmentsBranches = derived(
  [topFourDepartments, budgetBlocks, dataWithIsSelected],
  ([$topFourDepartments, $budgetBlocks, $dataWithIsSelected]) => {
    const budgetBlocksMax = max($budgetBlocks.map((dept) => dept.items.length))
    return traverseUntil({ children: $dataWithIsSelected }, (node) => (node.value)).map((node) => node.flat().flat().reduce(
      (result, leaf) => {
        const budgetBlocksForDept = $budgetBlocks.find((blocks) => blocks.name === leaf.department_name)
        const budgetBlocksCount = budgetBlocksForDept && budgetBlocksForDept.items && budgetBlocksForDept.items.length || 0
        const dept = {
          name: leaf.department_name,
          level: 'department',
          isSelected: $topFourDepartments
            .map((d) => d.Department)
            .includes(leaf.department_name),
          departmentType: types[leaf.department_name],
          budgetBlocks: budgetBlocksCount,
          isUserMax: budgetBlocksCount !== 0 && budgetBlocksCount === budgetBlocksMax,
        }
        return {
          ...dept,
          amount: result.amount + leaf.value * 1,
        }
      },
      { amount: 0 },
    ))
  })

export const unselectedDepartmentsSorted = derived(
  [departmentsBranches],
  ([$departmentsBranches]) => {
    return $departmentsBranches.filter((dept) => !dept.isSelected).sort((a, b) => b.amount - a.amount).map((dept, index) => ({
      ...dept,
      rank: index + 1,
    }))
})

export const selectedDepartmentsSorted = derived(
  [departmentsBranches],
  ([$departmentsBranches]) => {
    return $departmentsBranches.filter((dept) => dept.isSelected).sort((a, b) => b.amount - a.amount).map((dept, index) => ({
      ...dept,
      rank: index + 1,
    }))
})

export const deptsGroupedByType = derived(
  [departmentsBranches, unselectedDepartmentsSorted],
  ([$departmentsBranches, $unselectedDepartmentsSorted]) => {
    return Object.entries(
      $departmentsBranches.reduce((prev, curr) => ({
        ...prev,
        [
          `${curr.isSelected && 'Selected' || 'Unselected'} ${curr.departmentType}`]: (
            prev[`${curr.isSelected && 'Selected' || 'Unselected'} ${curr.departmentType}`] || []
          ).concat([curr])
      }), {}))
      .map(([name, children]) => ({
        name,
        children: children.map((dept) => ({
          ...dept,
          isHighest: dept.name === $unselectedDepartmentsSorted[0].name,
        })),
        level: "department_type_selection",
        departmentType: children[0].departmentType,
        isSelected: children[0].isSelected,
      })
    )
})

export const selectedDeptsGroupedByType = derived(
  [deptsGroupedByType],
  ([$deptsGroupedByType]) => $deptsGroupedByType.filter((deptsGroup) => deptsGroup.isSelected),
)

export const unselectedDeptsGroupedByType = derived(
  [deptsGroupedByType],
  ([$deptsGroupedByType]) => $deptsGroupedByType.filter((deptsGroup) => !deptsGroup.isSelected),
)

export const departmentLevelData = derived(
  [selectedDeptsGroupedByType, unselectedDeptsGroupedByType],
  ([$selectedDeptsGroupedByType, $unselectedDeptsGroupedByType]) => {
    return {
      name: "Houston Budget",
      level: "root",
      children: [
        {
          name: "Your Departments",
          level: "selection",
          children: $selectedDeptsGroupedByType,
        },
        {
          name: "Other Departments",
          level: "selection",
          children: $unselectedDeptsGroupedByType,
        },
      ],
    }
  }
)
const traverseChildrenToDepts = (deptFn) => (({ children, ...rest }) => ({
  ...rest,
  children: children.map(({ children, ...rest }) => ({
    ...rest,
    children: children.map(deptFn)
  })),
}))
export const budgetBlocksTreeDepts = derived(
  [departmentLevelData],
  ([$departmentLevelData]) => {
    return $departmentLevelData.children.map(traverseChildrenToDepts((dept) => ({
      ...dept,
      // sizing using budget blocks
      value: dept.isSelected && dept.budgetBlocks || 0,
      mode: 'budget-blocks',
    })))
  }
)
export const actualSelectedBudgetTreeDepts = derived(
  [departmentLevelData],
  ([$departmentLevelData]) => {
    return $departmentLevelData.children.map(traverseChildrenToDepts((dept) => ({
      ...dept,
      value: dept.isSelected && dept.amount || 0,
      mode: 'selected-budget',
    })))
  }
)
export const selectedWithHighestUnselected = derived(
  [departmentLevelData],
  ([$departmentLevelData]) => {
    return $departmentLevelData.children.map(traverseChildrenToDepts((dept) => ({
      ...dept,
      value: (dept.isSelected || dept.isHighest) && dept.amount || 0,
      mode: 'with-highest',
    })))
  }
)
export const otherDeptsGrouped = derived(
  [departmentLevelData],
  ([$departmentLevelData]) => {
    return $departmentLevelData.children.map(traverseChildrenToDepts((dept) => ({
      ...dept,
      value: dept.amount,
      mode: 'actual-budget',
    })))
  }
)
export const explore = derived(
  [dataWithIsSelected],
  ([$dataWithIsSelected]) => {
    return Object.entries($dataWithIsSelected
      .reduce((prev, curr) => ({
        ...prev,
        [
          `${curr.departmentType}`]: (
            prev[`${curr.departmentType}`] || []
          ).concat([curr])
      }), {}))
      .map(([name, children]) => ({
        name,
        children: children.map((dept) => ({
          ...dept,
        })),
        level: "department_type",
        departmentType: children[0].departmentType,
      }))
  }
)